import { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { Clear } from '@material-ui/icons'
import { editAuction, getAllEmployees } from '@data'
import { Spacings, Text } from '@styles'
import { Button, DrawerBodyWrapper, DrawerFooterWrapper, SectionHeader, Select, TOAST_TYPE } from '@base'
import { User } from '@data/model'
import { auctionStatus, strings } from '@constants'
import { useDispatch, useSelector } from 'react-redux'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'

const BodyWrapper = styled.div``
const SelectWrapper = styled.div``
const SelectedEmpoyee = styled.div`
	display: flex;
	align-items: center;
	font-size: ${Text.MEDIUM};
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	border-radius: ${Spacings.SPACING_2B};
	background: ${({ theme }) => theme.palette.background.blueLightest};
	margin: ${Spacings.SPACING_2B} 0;

	& > div {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}
`
const ClearIcon = styled(Clear)`
	cursor: pointer;
`

const MakePrimaryButton = styled.span`
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.palette.text.blue};
	border-radius: ${Spacings.SPACING_2B};
	border: 1px solid ${({ theme }) => theme.palette.text.blue};
	cursor: pointer;
	width: fit-content;
	margin-right: ${Spacings.SPACING_3B};

	${({ showAsText }) => {
		if (showAsText) {
			return css`
				cursor: initial;
				background: ${({ theme }) => theme.palette.background.messageRed};
				color: ${({ theme }) => theme.palette.text.white};
				border: none;
			`
		}
	}}
`

const AssignAuctionToEmployee = ({ auction, update }) => {
	const user = useSelector((state) => state.getIn(['user', 'user']))

	const dispatch = useDispatch()
	const [employees, setEmployees] = useState([])
	const [loading, setLoading] = useState(false)

	const primaryUser = useMemo(() => {
		if (auction?.relationship_manager && auction?.relationship_manager.length) {
			return auction.relationship_manager.find((_rm) => _rm.is_primary)
		}
		return null
	}, [auction])

	const alreadyAssignedUserIds = useMemo(() => {
		if (auction?.relationship_manager && auction?.relationship_manager.length) {
			return auction.relationship_manager.map((_rm) => _rm.user_id)
		}
		return null
	}, [auction])

	const selectableEmployees = employees.filter((_e) => !_e.selected)
	const selectedEmployees = employees.filter((_e) => _e.selected)

	// const isPrimaryUserSame = () => {
	// 	const selectedPrimaryUser = selectedEmployees.find((_e) => _e.isPrimary)
	// 	return selectedPrimaryUser && primaryUser && selectedPrimaryUser?.user_id === primaryUser?.user_id
	// }

	// const sameAsAlreadyAssigned = () => {
	// 	if (!alreadyAssignedUserIds) return false

	// 	const selectedIDs = selectedEmployees.map((_se) => _se.user_id)
	// 	return (
	// 		Boolean(selectedIDs.length === alreadyAssignedUserIds.length && selectedIDs.every((_id) => alreadyAssignedUserIds.includes(_id))) &&
	// 		isPrimaryUserSame()
	// 	)
	// }
	const disabled = selectedEmployees?.length === 0 || loading

	useEffect(() => {
		setLoading(true)
		getAllEmployees()
			.then((res) => {
				let employees = res
				// setting already assigned employees
				if (alreadyAssignedUserIds) {
					employees = employees.map((_e) => {
						if (alreadyAssignedUserIds.includes(_e.user_id)) {
							_e.selected = true

							// setting primary user
							if (primaryUser && primaryUser.user_id === _e.user_id) {
								_e.isPrimary = true
							}
						}
						return _e
					})
				}
				employees = employees.map((_e) => ({ ..._e, display_name_number: _e.full_name ?? _e.mobile_number }))
				setEmployees(employees)
			})
			.catch((err) => {
				console.log('Error: ', err)
			})
			.finally(() => setLoading(false))
	}, [])

	useEffect(() => {
		if (selectedEmployees.length === 0) return

		// If there no primary user, make the first one primary
		const primaryUser = selectedEmployees.find((_e) => _e.isPrimary)
		if (!primaryUser) {
			makePrimary(selectedEmployees[0]?.user_id)
		}
	}, [selectedEmployees])

	const onChange = (selectedEmpoyee) => {
		if (selectedEmpoyee) {
			setEmployees((_data) =>
				_data.map((_e) => {
					if (_e.user_id === selectedEmpoyee.user_id) {
						_e.selected = true
					}
					return _e
				})
			)
		}
	}

	const assignHandler = () => {
		setLoading(true)
		const relationshipManagers = selectedEmployees.map((_e) => {
			const _temp = new User(_e).getUserDetails()
			if (_e.isPrimary) {
				_temp.is_primary = true
			}
			return _temp
		})

		if (auction.auction_status === auctionStatus.created) {
			auction.auction_status = auctionStatus.unverified
		}

		auction.relationship_manager = relationshipManagers

		dispatch(showLoader(true))

		editAuction(auction, user)
			.then((res) => {
				if (res.status === 200) {
					update()
					dispatch(showToast(true, strings('msg_auction_assigned'), { type: TOAST_TYPE.SUCCESS }))
					dispatch(toggleDrawer(false))
				} else {
					return Promise.reject()
				}
			})
			.catch((err) => {
				dispatch(showToast(true, strings('msg_auction_unable_assign_auction'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
				setLoading(false)
			})
	}

	const removeHandler = (userId) => {
		if (!loading) {
			setEmployees((_data) =>
				_data.map((_e) => {
					if (_e.user_id === userId) {
						_e.selected = false
						if (_e.isPrimary) {
							_e.isPrimary = false
						}
					}
					return _e
				})
			)
		}
	}

	const makePrimary = (userId) => {
		if (!loading) {
			setEmployees((_data) =>
				_data.map((_e) => {
					_e.isPrimary = _e.user_id === userId
					return _e
				})
			)
		}
	}

	const renderSelectedEmpoyees = () =>
		selectedEmployees.map((_employee) => (
			<SelectedEmpoyee key={`employee-${_employee?.user_id}`} isPrimary={_employee?.isPrimary}>
				<div>
					<span>
						<b>{_employee.full_name}</b>
					</span>
					<span>
						<small>{_employee.mobile_number}</small>
					</span>
				</div>
				{!_employee?.isPrimary && (
					<MakePrimaryButton onClick={() => makePrimary(_employee.user_id)}>{strings('set_as_primary')}</MakePrimaryButton>
				)}
				{_employee?.isPrimary && <MakePrimaryButton showAsText>{strings('primary')}</MakePrimaryButton>}
				<ClearIcon fontSize='small' onClick={() => removeHandler(_employee.user_id)} />
			</SelectedEmpoyee>
		))

	return (
		<>
			<DrawerBodyWrapper>
				<BodyWrapper>
					<SelectWrapper>
						<Select
							disabled={loading}
							id='employee-select'
							placeholder={loading ? strings('loading_employees') : strings('select_employee')}
							data={selectableEmployees}
							displayKey='display_name_number'
							primaryKey='user_id'
							value={{}}
							onChange={onChange}
							label={`${strings('select_employee')}(s)`}
						/>
					</SelectWrapper>
					{renderSelectedEmpoyees()}
				</BodyWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button small uppercase type='primary' disabled={disabled} onClick={assignHandler}>
					{strings('assign')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default AssignAuctionToEmployee
